import { DatePipe, DOCUMENT, isPlatformBrowser, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID, Renderer2 } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { GigsumoConstants } from '../../../../helpers/gigsumoConstants';
import { getActiveUrgencyDays } from '../../../../helpers/jobUtils';
import { ApiService } from '../../../../service/api.service';
import { GigsumoService } from '../../../../service/gigsumo.service';
import { JobProvider } from '../job-card/job-card.component';
import { getJobParams } from '../job-listing/job-listing.component';

@Component({
  selector: 'app-job-details',
  standalone: true,
  imports: [NgIf, NgFor, NgClass, DatePipe],
  templateUrl: './job-details.component.html',
  styleUrl: './job-details.component.scss'
})
export class JobDetailsComponent implements OnInit {
  private routeSub = new Subscription;
  ENVIRONMENT = environment;
  constructor(
    private apiService: ApiService,
    private _activateRoute: ActivatedRoute,
    private meta: Meta,
    public jobData: JobProvider,
    private gigsumoService: GigsumoService,
    private title: Title,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object) {
    this.setMetaTags()
    this.setStructuredData();
    this.setBreadcrumbStructuredData();
  }
  jobId: any;
  ngOnInit(): void {
    this.routeSub = this._activateRoute.paramMap.subscribe(params => {
      this.jobId = params.get('jobId'); // Get the jobId from the URL
    });



    this.jobTitle = this.jobData.getJob?.jobTitle
    this.companyName = this.jobData.getJob?.jobPostedBehalfOf
    this.location = this.jobData.getJob?.city + ', ' + this.jobData.getJob?.state
    if (this.jobDetails && this.jobData.getJob?.primarySkills) {
      this.jobData.getJob?.primarySkills.forEach((ele: any, index: number) => {
        if (index == 0) {
          this.skills = this.skills + ele
        } else {
          this.skills = this.skills + ', ' + ele
        }
      })
    }
    this.yearsOfExperience = this.jobData.getJob?.experienceFrom
  }

  // New design: formatted pay-rate range. MUST be a getter (not precomputed in
  // ngOnInit) because this component is a singleton reused across job selections —
  // a stored value would freeze on the first job.
  get rateText(): string {
    return this.gigsumoService.formatRateRange(
      this.jobData.getJob?.targetRateFrom,
      this.jobData.getJob?.targetRateTo,
      this.jobData.getJob?.country,
      this.jobData.getJob?.payType
    );
  }

  // ===== New design presentational helpers =====
  getStatusClass(status: string): string {
    return this.gigsumoService.getJobStatusClass(status);
  }

  getCompanyInitials(): string {
    return this.gigsumoService.getInitials(this.jobData.getJob?.jobPostedBehalfOf || '');
  }

  /** Recruiter/company current-org location, if available on the user record. */
  getCompanyLocation(): string {
    const exp = this.jobData.getJob?.user?.workExperience;
    if (Array.isArray(exp)) {
      const current = exp.find((e: any) => e.currentOrganization === true);
      if (current) {
        const parts = [current.city, current.state].filter(Boolean);
        return parts.join(', ');
      }
    }
    return '';
  }

  /** Friendly label for the poster's user type (org-type). */
  getOrgTypeLabel(): string {
    const map: { [k: string]: string } = {
      RECRUITER: 'Recruiter',
      FREELANCE_RECRUITER: 'Freelance Recruiter',
      BENCH_RECRUITER: 'Bench Sales',
      JOB_SEEKER: 'Job Seeker',
      MANAGEMENT_TALENT_ACQUISITION: 'Management Talent Acquisition',
    };
    const t = this.jobData.getJob?.jobPostedByUserType;
    return (t && map[t]) || '';
  }

  /** Share the current job URL (Web Share API, falls back to clipboard). */
  onShare(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const url = window.location.href;
    const title = this.jobData.getJob?.jobTitle || 'Job';
    const nav: any = window.navigator;
    if (nav?.share) {
      nav.share({ title, url }).catch(() => { /* user cancelled */ });
    } else if (nav?.clipboard) {
      nav.clipboard.writeText(url);
    }
  }

  /** Save action requires login — stubbed for now (UI-only). */
  onSave(): void {
    // Deferred: full save needs an authenticated session.
  }
 

  jobDetails: any = {};
  url: string = environment.serverUrl + 'download/';
  jobTitle: string = '';
  companyName: string = '';
  location: string = '';
  skills: string = ''
  yearsOfExperience: string = ''
  setMetaTags(): void {
    const pageTitle = `${this.jobData.getJob?.jobTitle} in ${this.jobData.getJob?.city}, ${this.jobData.getJob?.state}  - ${this.jobData.getJob?.jobPostedBehalfOf}`;
    const description = `Join ${this.jobData.getJob?.jobPostedBehalfOf} as a ${this.jobData.getJob?.jobTitle}. Required skills: ${this.jobData.getJob?.primarySkills}. Location: ${this.jobData.getJob?.city}, ${this.jobData.getJob?.state}. ${this.jobData.getJob?.experienceFrom}+ years of experience preferred.`;

    // Set page title
    this.title.setTitle(pageTitle);
    this.meta.addTags([
      { name: 'description', content: `Looking for a ${this.jobData.getJob?.jobTitle} in ${this.jobData.getJob?.city}, ${this.jobData.getJob?.state} with ${this.jobData.getJob?.primarySkills} and ${this.jobData.getJob?.experienceTo} years of experience.` },
      { name: 'keywords', content: `${this.jobData.getJob?.jobTitle}, ${this.jobData.getJob?.city}, ${this.jobData.getJob?.state}, ${this.jobData.getJob?.primarySkills}, ${this.jobData.getJob?.experienceTo} years experience` },
      { property: 'og:title', content: `${this.jobData.getJob?.jobTitle} - ${this.jobData.getJob?.city}, ${this.jobData.getJob?.state}` },
      { property: 'og:description', content: `A job opportunity for ${this.jobData.getJob?.jobTitle} with ${this.jobData.getJob?.primarySkills} skills and ${this.jobData.getJob?.experienceTo} years of experience.` },
      { property: 'og:type', content: 'website' },
      { property: 'og:image', content: 'https://www.gigsumo.com/images/gigsumologo_bigface.png' },
      { property: 'og:type', content: 'website' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: pageTitle },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: 'https://www.gigsumo.com/images/gigsumologo_bigface.png' },
      { name: 'twitter:creator', content: '@GigSumoHQ' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
      { charset: 'UTF-8' },
      { name: 'robots', content: 'index, follow' },
      { name: 'author', content: 'Gigsumo Inc.' }
    ])
    if (isPlatformBrowser(this.platformId)) {
      this.meta.addTags([
        { property: 'og:url', content: window.location.href }
      ])
    }
  }

  private setStructuredData(): void {
    const script = this.renderer.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      'title': this.jobTitle,
      'description': `Join ${this.companyName} as a ${this.jobTitle}. Required skills: ${this.skills}. Location: ${this.location}. ${this.yearsOfExperience}+ years of experience preferred.`,
      'identifier': {
        '@type': 'PropertyValue',
        'name': this.companyName,
        'value': '12345'
      },
      'datePosted': new Date().toISOString().split('T')[0], // Current date
      'jobLocation': {
        '@type': 'Place',
        'address': {
          '@type': 'PostalAddress',
          'streetAddress': '123 Tech Street',
          'addressLocality': this.location.split(',')[0],
          'addressRegion': this.location.split(',')[1]?.trim() || '',
          'postalCode': this.jobData.getJob?.zipCode,
          'addressCountry': this.jobData.getJob?.country
        }
      },
      'hiringOrganization': {
        '@type': 'Organization',
        'name': this.companyName,
        'sameAs': 'https://www.companywebsite.com'
      },
      'baseSalary': {
        '@type': 'MonetaryAmount',
        'currency': 'US Dollars',
        'value': {
          '@type': 'QuantitativeValue',
          'value': this.jobData.getJob?.targetRateTo,
          'unitText': this.jobData.getJob?.createdOn
        }
      }
    });
    if (isPlatformBrowser(this.platformId)) {
      this.renderer.appendChild(document.head, script);
    }
  }

  /** BreadcrumbList structured data for Google SERP (Home › Jobs › title). */
  private setBreadcrumbStructuredData(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const origin = window.location.origin;
    const url = window.location.href;
    const title = this.jobData.getJob?.jobTitle || 'Job';
    const script = this.renderer.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org/',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        { '@type': 'ListItem', position: 1, name: 'Home', item: origin + '/' },
        { '@type': 'ListItem', position: 2, name: 'Jobs', item: origin + '/jobs' },
        { '@type': 'ListItem', position: 3, name: title, item: url }
      ]
    });
    this.renderer.appendChild(this.document.head, script);
  }

  getBusinessLogo() {
    const workExperience = this.jobData.getJob?.user?.workExperience;

    if (workExperience?.length) {
      const currentOrg = workExperience.find((exp: any) => exp.currentOrganization === true);

      if (currentOrg?.businessLogo) {
        return this.ENVIRONMENT.serverUrl + 'download/' + currentOrg.businessLogo;
      }
    }

    return "../../../../assets/params/images/logo/company.png";
  }

  getActiveUrgencyDays(job: any): number | null {
    return getActiveUrgencyDays(job);
  }

  getLoginParams(job:any){
     return getJobParams(job);
  }
}
